import { InventoryItem } from '../models/inventory/inventory-item.interface';
import { InventoryItemType, normalizeInventoryItemType } from '../enums/inventory-item-type.enum';

// Generic inventory-item option used by the global typeahead (sales-order lines, shipments,
// invoices, composite component picker, etc.). Pre-computes the display fields so consumers
// never call methods from the template.
export interface InventoryItemSearchOption {
  id: string;
  number: string;
  displayName: string;
  title: string;
  subtitle: string;
  currencyCode: string | null;
  isComposite: boolean;
  /**
   * Wire-format inventory-item type (`Product` | `Service` | `Hybrid`). Carried on the
   * option so callers (sales-order lines, shipment modals) can branch on the line's
   * source-item type without re-fetching the inventory item. Defaults to `Product`
   * when the source payload is missing the field.
   */
  itemType: InventoryItemType;
  // Base unit-of-measure code for the inventory item. Populated when the source
  // payload carries `unitsOfMeasure` (or the `unit` fallback), so the composite
  // components picker can preview the selected child's UoM without a follow-up
  // request. Optional because paged list sources may omit unit-of-measure metadata.
  baseUomCode?: string;
  // Base unit price for the inventory item. Used by the composite components
  // expansion on sales-order lines to show each child's contribution
  // (`quantity * unitPrice`) without a follow-up request per child.
  unitPrice?: number;
}

export function buildInventoryItemOption(item: InventoryItem): InventoryItemSearchOption {
  const number = item.number?.trim() ?? '';
  const displayName = item.displayName?.trim() ?? '';
  const title = displayName.length > 0 ? displayName : number;
  const baseUomCode = resolveBaseUomCode(item);
  const option: InventoryItemSearchOption = {
    id: item.id,
    number,
    displayName,
    title,
    subtitle: number,
    currencyCode: item.currencyCode?.trim() ?? null,
    isComposite: item.isComposite === true,
    itemType: normalizeInventoryItemType(item.itemType),
  };
  if (baseUomCode.length > 0) {
    option.baseUomCode = baseUomCode;
  }
  if (Number.isFinite(item.unitPrice)) {
    option.unitPrice = item.unitPrice;
  }
  return option;
}

function resolveBaseUomCode(item: InventoryItem): string {
  const baseUomCode = item.unitsOfMeasure?.find(
    (candidate) => candidate.unitOfMeasureId === item.baseUnitOfMeasureId,
  )?.code;
  if (baseUomCode && baseUomCode.trim().length > 0) {
    return baseUomCode.trim();
  }
  const unit = item.unit?.trim();
  return unit ?? '';
}

export interface InventoryItemOptionFilterConfig {
  // Drops candidates whose `isComposite` flag is true. Used by the composite-item
  // child picker so users cannot pick a composite as a child component (the backend
  // rejects it anyway with a 409, but we should not offer the option in the UI).
  excludeComposite?: boolean;
}

export function inventoryItemOptionFilter(
  option: InventoryItemSearchOption,
  term: string,
  config?: InventoryItemOptionFilterConfig,
): boolean {
  if (config?.excludeComposite && option.isComposite) {
    return false;
  }

  const normalized = term.trim().toLowerCase();
  if (normalized.length === 0) {
    return true;
  }

  return (
    option.title.toLowerCase().includes(normalized) ||
    option.subtitle.toLowerCase().includes(normalized) ||
    option.number.toLowerCase().includes(normalized)
  );
}

export function inventoryItemOptionFormatter(option: InventoryItemSearchOption | null | undefined): string {
  return option?.title ?? '';
}

export function inventoryItemOptionCompare(
  a: InventoryItemSearchOption,
  b: InventoryItemSearchOption,
): boolean {
  return a.id === b.id;
}
